import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSocketContext, getEnabledPlayers } from '@swtorbit/shared-react-hooks';
import {
  SocketEvent,
  DiscordPlayerLinkedResponse,
  GameType,
  GameDiscordSettings,
  DiscordGameSettingsPatch,
} from '@swtorbit/shared-socket-events';
import type {
  DiscordChannel,
  DiscordRole,
  DiscordTestAlertType,
  DiscordTestAlertResultResponse,
} from '@swtorbit/shared-socket-events';
import { useGameContext } from '../contexts/GameContext';
import { useDiscordContext } from '../contexts/DiscordContext';
import { DiscordRolePicker, TeamChannelSettings, VoiceChannelSelect } from './discord/TeamChannelSettings';

// ============================================================================
// Per-game-type settings initialisation
// ============================================================================

/**
 * Build the form state from the game's saved settings. Fields are picked by
 * the game's type — the saved settings object carries no reliable marker of
 * which game type it belongs to. Toggles default to off, matching the server.
 *
 * Form state uses DiscordGameSettingsPatch: a channel/role set to `null` means
 * the admin chose "none", which the server treats as "clear the saved value".
 */
function buildDiscordSettingsState(
  gameType: GameType,
  discord: GameDiscordSettings | undefined
): DiscordGameSettingsPatch {
  const saved: DiscordGameSettingsPatch | undefined = discord;
  const base: DiscordGameSettingsPatch = {
    lobbyVoiceChannelId: saved?.lobbyVoiceChannelId,
    alertsTextChannelId: saved?.alertsTextChannelId,
    channelCategoryId: saved?.channelCategoryId,
    channelSource: saved?.channelSource,
    roleSource: saved?.roleSource,
    autoCreateChannels: saved?.autoCreateChannels ?? false,
    channelMovementEnabled: saved?.channelMovementEnabled ?? false,
    alertsEnabled: saved?.alertsEnabled ?? false,
    alertRoundStart: saved?.alertRoundStart ?? false,
    alertRoundEnd: saved?.alertRoundEnd ?? false,
    alertGameEnd: saved?.alertGameEnd ?? false,
  };

  switch (gameType) {
    case GameType.HIDE_AND_SEEK:
      return {
        ...base,
        alertPlayerCaught: saved?.alertPlayerCaught ?? false,
        hnsHidersVoiceChannelId: saved?.hnsHidersVoiceChannelId,
        hnsSeekersVoiceChannelId: saved?.hnsSeekersVoiceChannelId,
      };
    case GameType.FP_RACE:
      return {
        ...base,
        fprLeaderboardChannelId: saved?.fprLeaderboardChannelId,
        officerRoleId: saved?.officerRoleId,
        teamChannelPool: saved?.teamChannelPool,
        teamLinkOverrides: saved?.teamLinkOverrides,
      };
    case GameType.TRAILBLAZERS:
      return {
        ...base,
        officerRoleId: saved?.officerRoleId,
        teamChannelPool: saved?.teamChannelPool,
        teamLinkOverrides: saved?.teamLinkOverrides,
      };
    case GameType.HIDE_AND_SEEK_TEAMS:
      return {
        ...base,
        alertPlayerCaught: saved?.alertPlayerCaught ?? false,
        officerRoleId: saved?.officerRoleId,
        hnstTeamLinks: saved?.hnstTeamLinks,
      };
  }
}

// ============================================================================
// Per-game-type channel settings (the team channels themselves are
// TeamChannelSettings)
// ============================================================================

type SetGameSettings = React.Dispatch<React.SetStateAction<DiscordGameSettingsPatch>>;

const MOVEMENT_HINTS: Record<GameType, { movement: string; lobby: string }> = {
  [GameType.HIDE_AND_SEEK]: {
    movement: 'Automatically move linked players when rounds start/end',
    lobby: 'Permanent channel where players are moved after rounds end',
  },
  [GameType.FP_RACE]: {
    movement: 'Automatically move players to their team channel when rounds start, and back to lobby when they finish',
    lobby: 'Channel where all teams gather between rounds',
  },
  [GameType.TRAILBLAZERS]: {
    movement: 'Automatically move players to their team channel when the game starts, and back to lobby when it ends',
    lobby: 'Channel where all teams gather',
  },
  [GameType.HIDE_AND_SEEK_TEAMS]: {
    movement: 'Automatically move players to their team channel on entering the lobby, and back to lobby when the game ends',
    lobby: 'Channel where all teams gather',
  },
};

function renderMovementSettings(
  gameType: GameType,
  gameSettings: DiscordGameSettingsPatch,
  setGameSettings: SetGameSettings,
  voiceChannels: DiscordChannel[],
): React.ReactNode {
  return (
    <>
      <div className="field checkbox-group">
        <label>
          <input
            className="checkbox"
            type="checkbox"
            checked={gameSettings.channelMovementEnabled ?? false}
            onChange={(e) => setGameSettings(prev => ({
              ...prev,
              channelMovementEnabled: e.target.checked,
            }))}
          />
          Enable channel movement
        </label>
        <span className="field__hint">{MOVEMENT_HINTS[gameType].movement}</span>
      </div>

      {gameSettings.channelMovementEnabled && (
        <div className="sub-options">
          <div className="field sub-option">
            <label htmlFor="lobby-channel">Lobby Voice Channel:</label>
            <VoiceChannelSelect
              id="lobby-channel"
              channels={voiceChannels}
              value={gameSettings.lobbyVoiceChannelId}
              onChange={(id) => setGameSettings(prev => ({ ...prev, lobbyVoiceChannelId: id }))}
            />
            <span className="field__hint">{MOVEMENT_HINTS[gameType].lobby}</span>
          </div>
        </div>
      )}
    </>
  );
}

function renderOfficerRole(
  gameSettings: DiscordGameSettingsPatch,
  setGameSettings: SetGameSettings,
  roles: DiscordRole[],
): React.ReactNode {
  return (
    <div className="field sub-option">
      <span className="field__label">Officer Role (optional):</span>
      <DiscordRolePicker
        roles={roles}
        value={gameSettings.officerRoleId}
        noneLabel="-- No officer role --"
        requireAssignable={false}
        onChange={(roleId) => setGameSettings(prev => ({ ...prev, officerRoleId: roleId }))}
      />
      <span className="field__hint">
        Members with this role can view and join all the team voice channels SWTORBIT creates
      </span>
    </div>
  );
}

function renderFprLeaderboard(
  gameSettings: DiscordGameSettingsPatch,
  setGameSettings: SetGameSettings,
  textChannels: DiscordChannel[],
): React.ReactNode {
  return (
    <div className="field sub-option">
      <label htmlFor="fpr-leaderboard-channel">Leaderboard Channel:</label>
      <select
        className="select"
        id="fpr-leaderboard-channel"
        value={gameSettings.fprLeaderboardChannelId || ''}
        onChange={(e) => setGameSettings(prev => ({
          ...prev,
          fprLeaderboardChannelId: e.target.value || null,
        }))}
      >
        <option value="">-- Select channel --</option>
        {textChannels.map((channel) => (
          <option key={channel.id} value={channel.id}>
            #{channel.name}
          </option>
        ))}
      </select>
      <span className="field__hint">
        Text channel where live scoreboard and race updates will be posted (coming soon)
      </span>
    </div>
  );
}

// Exhaustive dispatcher — TypeScript will error if a new GameType is added without an entry
const GAME_TYPE_EXTRA_SETTINGS: Record<
  GameType,
  (
    gameSettings: DiscordGameSettingsPatch,
    setGameSettings: SetGameSettings,
    textChannels: DiscordChannel[],
    roles: DiscordRole[],
  ) => React.ReactNode
> = {
  [GameType.HIDE_AND_SEEK]: () => null,
  [GameType.FP_RACE]: (gs, set, tc, roles) => (
    <>
      {renderFprLeaderboard(gs, set, tc)}
      {renderOfficerRole(gs, set, roles)}
    </>
  ),
  [GameType.TRAILBLAZERS]: (gs, set, _tc, roles) => renderOfficerRole(gs, set, roles),
  [GameType.HIDE_AND_SEEK_TEAMS]: (gs, set, _tc, roles) => renderOfficerRole(gs, set, roles),
};

// Exhaustive dispatcher for "Player caught" alert option — only HNS and HNST have this
function renderGameTypeAlertOptions(
  gameType: GameType,
  gameSettings: DiscordGameSettingsPatch,
  setGameSettings: SetGameSettings,
): React.ReactNode {
  switch (gameType) {
    case GameType.HIDE_AND_SEEK:
    case GameType.HIDE_AND_SEEK_TEAMS: {
      return (
        <label className="d-flex align-items-center gap-xs">
          <input
            className="checkbox"
            type="checkbox"
            checked={gameSettings.alertPlayerCaught ?? false}
            onChange={(e) => setGameSettings(prev => ({
              ...prev,
              alertPlayerCaught: e.target.checked,
            }))}
          />
          <span>Player caught alerts</span>
        </label>
      );
    }
    case GameType.FP_RACE:
      return null;
    case GameType.TRAILBLAZERS:
      return null;
  }
}

/**
 * Does this game type actually send round-start / round-end alerts?
 *
 * Trailblazers does not: it isn't round-cyclic (a "round" is derived graph
 * depth, not a scheduled phase), and no server code reads `alertRoundStart` /
 * `alertRoundEnd` for it. Hiding the toggles keeps the tab honest instead of
 * offering controls that do nothing.
 */
function gameTypeSendsRoundAlerts(gameType: GameType): boolean {
  switch (gameType) {
    case GameType.HIDE_AND_SEEK:
    case GameType.HIDE_AND_SEEK_TEAMS:
    case GameType.FP_RACE:
      return true;
    case GameType.TRAILBLAZERS:
      return false;
  }
}

/**
 * Alert types the "Send test" dropdown offers, limited to the ones this game
 * type can really send — the server rejects the rest.
 */
function getTestAlertTypes(gameType: GameType): DiscordTestAlertType[] {
  switch (gameType) {
    case GameType.HIDE_AND_SEEK:
    case GameType.HIDE_AND_SEEK_TEAMS:
      return ['round_start', 'round_end', 'game_end', 'player_caught'];
    case GameType.FP_RACE:
      return ['round_start', 'flashpoint_reveal', 'round_end', 'game_end'];
    case GameType.TRAILBLAZERS:
      return ['game_end'];
  }
}

const TEST_ALERT_LABELS: Record<DiscordTestAlertType, string> = {
  round_start: 'Round start',
  flashpoint_reveal: 'Flashpoint reveal',
  round_end: 'Round end',
  game_end: 'Game end',
  player_caught: 'Player caught',
};

// ============================================================================
// Component
// ============================================================================

export function DiscordSettings() {
  const { socket } = useSocketContext();
  const { selectedGame } = useGameContext();
  const {
    isLinked,
    discordUser,
    guilds,
    isLoadingGuilds,
    channels,
    isLoadingChannels,
    members,
    isLoadingMembers,
    roles,
    fetchGuilds,
    fetchChannelsAndMembers,
    selectGuild,
    installBot,
    isInstallingBot,
  } = useDiscordContext();

  // Derive the selected guild from the game's discord settings
  const selectedGuild = useMemo(() => {
    const guildId = selectedGame?.discord?.guildId;
    if (!guildId) return null;
    return guilds.find(g => g.id === guildId) || null;
  }, [selectedGame?.discord?.guildId, guilds]);

  // When the game has a guild selected, fetch channels/members for it
  useEffect(() => {
    const guildId = selectedGame?.discord?.guildId;
    if (guildId) {
      fetchChannelsAndMembers(guildId);
    }
  }, [selectedGame?.discord?.guildId, fetchChannelsAndMembers]);

  const enabledPlayers = useMemo(() => getEnabledPlayers(selectedGame), [selectedGame?.players]);

  const [isEditingGuild, setIsEditingGuild] = useState(false);

  const [gameSettings, setGameSettings] = useState<DiscordGameSettingsPatch>({});
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [testAlertType, setTestAlertType] = useState<DiscordTestAlertType>('round_start');
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [testMessage, setTestMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [manualLinkPlayer, setManualLinkPlayer] = useState<string | null>(null);
  const [selectedMember, setSelectedMember] = useState<typeof members[0] | null>(null);
  const [memberSearch, setMemberSearch] = useState('');
  const [isManualLinking, setIsManualLinking] = useState(false);
  const [showMemberDropdown, setShowMemberDropdown] = useState(false);

  useEffect(() => {
    if (!socket) return;

    const handleSettingsUpdated = (data: { success: boolean; error?: string }) => {
      setIsSavingSettings(false);
      if (data.success) {
        setSaveMessage({ type: 'success', text: 'Discord settings saved successfully!' });
        setTimeout(() => setSaveMessage(null), 3000);
      } else {
        setSaveMessage({ type: 'error', text: data.error || 'Failed to save settings' });
      }
    };

    const handlePlayerLinked = (data: DiscordPlayerLinkedResponse) => {
      if (!data.success) return;
      setIsManualLinking(false);
      setManualLinkPlayer(null);
      setSelectedMember(null);
      setMemberSearch('');
      setShowMemberDropdown(false);
    };

    const handleTestAlertResult = (data: DiscordTestAlertResultResponse) => {
      setIsSendingTest(false);
      if (data.success) {
        setTestMessage({ type: 'success', text: 'Test alert sent to Discord.' });
      } else {
        setTestMessage({ type: 'error', text: data.error || 'Failed to send test alert' });
      }
      setTimeout(() => setTestMessage(null), 4000);
    };

    socket.on(SocketEvent.DISCORD_SETTINGS_UPDATED, handleSettingsUpdated);
    socket.on(SocketEvent.DISCORD_PLAYER_LINKED, handlePlayerLinked);
    socket.on(SocketEvent.DISCORD_TEST_ALERT_RESULT, handleTestAlertResult);

    return () => {
      socket.off(SocketEvent.DISCORD_SETTINGS_UPDATED, handleSettingsUpdated);
      socket.off(SocketEvent.DISCORD_PLAYER_LINKED, handlePlayerLinked);
      socket.off(SocketEvent.DISCORD_TEST_ALERT_RESULT, handleTestAlertResult);
    };
  }, [socket]);

  // Reinitialise settings state whenever the selected game changes
  useEffect(() => {
    if (selectedGame) {
      setGameSettings(buildDiscordSettingsState(selectedGame.gameType, selectedGame.discord));
    } else {
      setGameSettings({});
    }
  }, [selectedGame]);

  const handleChangeServer = useCallback(() => {
    setIsEditingGuild(true);
    fetchGuilds(true);
  }, [fetchGuilds]);

  const handleSelectGuild = useCallback((guildId: string) => {
    if (!selectedGame) return;
    selectGuild(guildId, selectedGame.roomCode);
    setIsEditingGuild(false);
  }, [selectGuild, selectedGame]);

  const handleInstallBot = useCallback((guildId: string) => {
    installBot(guildId);
  }, [installBot]);

  const handleSaveSettings = useCallback(() => {
    if (!socket || !selectedGame || !selectedGuild) return;

    setIsSavingSettings(true);
    setSaveMessage(null);

    socket.emit(SocketEvent.DISCORD_UPDATE_GAME_SETTINGS, {
      roomCode: selectedGame.roomCode,
      settings: {
        guildId: selectedGuild.id,
        ...gameSettings,
      },
    });
  }, [socket, selectedGame, selectedGuild, gameSettings]);

  // Alert types this game type can send, and the selection clamped to them —
  // the stored `testAlertType` may be a leftover from a different game type
  // (default 'round_start' is not offered for TRL, for instance).
  const testAlertTypes = useMemo(
    () => (selectedGame ? getTestAlertTypes(selectedGame.gameType) : []),
    [selectedGame],
  );
  const effectiveTestAlertType = testAlertTypes.includes(testAlertType)
    ? testAlertType
    : testAlertTypes[0];

  const handleSendTest = useCallback(() => {
    if (!socket || !selectedGame || !effectiveTestAlertType) return;
    setIsSendingTest(true);
    setTestMessage(null);
    socket.emit(SocketEvent.DISCORD_SEND_TEST_ALERT, {
      roomCode: selectedGame.roomCode,
      alertType: effectiveTestAlertType,
    });
  }, [socket, selectedGame, effectiveTestAlertType]);

  const voiceChannels = useMemo(() => channels.filter(c => c.type === 'voice'), [channels]);
  const textChannels = useMemo(() => channels.filter(c => c.type === 'text'), [channels]);
  const categories = useMemo(() => channels.filter(c => c.type === 'category'), [channels]);

  const filteredMembers = useMemo(() => {
    if (!memberSearch) return members;
    const searchLower = memberSearch.toLowerCase();
    return members.filter(m =>
      m.displayName.toLowerCase().includes(searchLower) ||
      m.username.toLowerCase().includes(searchLower)
    );
  }, [members, memberSearch]);

  if (!isLinked) {
    return (
      <div className="d-flex flex-column gap-base">
        <h2>🎮 Discord Integration</h2>
        <div className="alert">
          <p>Link your Discord account using the button in the header to enable voice channel management.</p>
          <p className="field__hint">Once linked, you can configure server and channel settings here.</p>
        </div>
      </div>
    );
  }

  if (!selectedGame) {
    return (
      <div className="d-flex flex-column gap-base">
        <h2>🎮 Discord Integration</h2>
        <p className="d-flex align-items-center gap-xs">✅ Discord: {discordUser?.username}</p>
        <div className="alert">
          <p>Select a game from the header to configure Discord settings.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column gap-base">
      <h2>🎮 Discord Integration</h2>
      <p className="d-flex align-items-center gap-xs">✅ Discord: {discordUser?.username} • Game: {selectedGame.name}</p>

      {/* Guild Selection Section */}
      <div className="card card--bg-midnight card--border card--border-navy">
        <h3>Discord Server</h3>

        {isLoadingGuilds ? (
          <p>Loading servers...</p>
        ) : guilds.length === 0 ? (
          <div className="text-slate fs-sm">
            <p>No Discord servers found where you have admin permissions.</p>
            <p className="field__hint">Make sure you&apos;re an admin in at least one Discord server.</p>
          </div>
        ) : selectedGuild && !isEditingGuild ? (
          <div className="guild-selected">
            <div className="list__item is-selected">
              <div className="d-flex align-items-center gap-sm">
                {selectedGuild.icon ? (
                  <img src={selectedGuild.icon} alt="" className="rounded-full" />
                ) : (
                  <div className="badge badge--pill badge--slate">
                    {selectedGuild.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <span className="list__title">{selectedGuild.name}</span>
                <span className="badge badge--sm text-green">✓ Bot installed</span>
              </div>
              <button
                onClick={handleChangeServer}
                className="btn btn--sm btn--outline btn--slate"
              >
                Change Server
              </button>
            </div>
          </div>
        ) : (
          <div className="list list--divided list--hover d-flex flex-column gap-xs">
            <p className="field__hint">Select a server to configure Discord integration. Servers without the bot will show an install button.</p>

            {guilds.map((guild) => (
              <div
                key={guild.id}
                className={`list__item ${selectedGuild?.id === guild.id ? 'is-selected' : ''} ${!guild.botInstalled ? 'o-60' : ''}`}
              >
                <div className="d-flex align-items-center gap-sm">
                  {guild.icon ? (
                    <img src={guild.icon} alt="" className="rounded-full" />
                  ) : (
                    <div className="badge badge--pill badge--slate">
                      {guild.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span className="list__title">{guild.name}</span>
                </div>

                <div className="d-flex align-items-center gap-sm">
                  {guild.botInstalled ? (
                    <>
                      <span className="badge badge--sm text-green">✓ Bot installed</span>
                      <button
                        onClick={() => handleSelectGuild(guild.id)}
                        disabled={selectedGuild?.id === guild.id}
                        className="btn btn--sm"
                      >
                        {selectedGuild?.id === guild.id ? 'Selected' : 'Select'}
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="badge badge--sm text-red">Bot not installed</span>
                      <button
                        onClick={() => handleInstallBot(guild.id)}
                        disabled={isInstallingBot === guild.id}
                        className="btn btn--sm btn--discord"
                      >
                        {isInstallingBot === guild.id ? 'Installing...' : '🤖 Install Bot'}
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}

            {isEditingGuild && selectedGuild && (
              <button
                onClick={() => setIsEditingGuild(false)}
                className="btn btn--sm btn--outline btn--slate"
                style={{ marginTop: '0.5rem' }}
              >
                Cancel
              </button>
            )}
          </div>
        )}
      </div>

      {/* Game Channel Settings Section */}
      {selectedGuild && (
        <div className="card card--bg-midnight card--border card--border-navy">
          <h3>Channel Settings</h3>

          <div className="p-base">
            {isLoadingChannels ? (
              <p>Loading channels...</p>
            ) : (
              <>
                {/* Team (or Hiders/Seekers) channels and roles: made, linked or off */}
                <TeamChannelSettings
                  game={selectedGame}
                  settings={gameSettings}
                  setSettings={setGameSettings}
                  voiceChannels={voiceChannels}
                  categories={categories}
                  roles={roles}
                />
                {renderMovementSettings(selectedGame.gameType, gameSettings, setGameSettings, voiceChannels)}
                {GAME_TYPE_EXTRA_SETTINGS[selectedGame.gameType](gameSettings, setGameSettings, textChannels, roles)}

                {/* Alerts — shared across all game types */}
                <div className="d-flex align-items-center justify-content-between gap-base">
                  <label className="d-flex align-items-center gap-xs">
                    <input
                      className="checkbox"
                      type="checkbox"
                      checked={gameSettings.alertsEnabled ?? false}
                      onChange={(e) => setGameSettings(prev => ({
                        ...prev,
                        alertsEnabled: e.target.checked,
                      }))}
                    />
                    <span>Enable alerts</span>
                  </label>

                  <div className="d-flex align-items-center gap-xs">
                    <select
                      className="select"
                      value={effectiveTestAlertType ?? ''}
                      onChange={(e) => setTestAlertType(e.target.value as DiscordTestAlertType)}
                      disabled={isSendingTest || testAlertTypes.length <= 1}
                    >
                      {testAlertTypes.map((type) => (
                        <option key={type} value={type}>
                          {TEST_ALERT_LABELS[type]}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={handleSendTest}
                      disabled={isSendingTest || !gameSettings.alertsTextChannelId}
                      className="btn btn--sm btn--outline btn--slate"
                      title={
                        !gameSettings.alertsTextChannelId
                          ? 'Select a text channel first'
                          : 'Send a dummy alert to preview formatting'
                      }
                    >
                      {isSendingTest ? 'Sending...' : 'Send test'}
                    </button>
                  </div>
                </div>
                <span className="field__hint">
                  Send game event embeds to the text channel
                </span>

                {testMessage && (
                  <div className={`message ${testMessage.type}`}>
                    {testMessage.text}
                  </div>
                )}

                {gameSettings.alertsEnabled && (
                  <div className="d-flex flex-column gap-xs">
                    <div className="field">
                      <label htmlFor="text-channel">Text Channel:</label>
                      <select
                        className="select"
                        id="text-channel"
                        value={gameSettings.alertsTextChannelId || ''}
                        onChange={(e) => setGameSettings(prev => ({
                          ...prev,
                          alertsTextChannelId: e.target.value || null,
                        }))}
                      >
                        <option value="">-- Select channel --</option>
                        {textChannels.map((channel) => (
                          <option key={channel.id} value={channel.id}>
                            #{channel.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    {/* Round alerts are hidden for game types that never send
                        them (TRL) — the toggles would be dead controls. */}
                    {gameTypeSendsRoundAlerts(selectedGame.gameType) && (
                      <label className="d-flex align-items-center gap-xs">
                        <input
                          className="checkbox"
                          type="checkbox"
                          checked={gameSettings.alertRoundStart ?? false}
                          onChange={(e) => setGameSettings(prev => ({
                            ...prev,
                            alertRoundStart: e.target.checked,
                          }))}
                        />
                        <span>Round start alerts</span>
                      </label>
                    )}
                    {/* Game-type-specific alert options (e.g. player caught for HNS) */}
                    {renderGameTypeAlertOptions(selectedGame.gameType, gameSettings, setGameSettings)}
                    {gameTypeSendsRoundAlerts(selectedGame.gameType) && (
                      <label className="d-flex align-items-center gap-xs">
                        <input
                          className="checkbox"
                          type="checkbox"
                          checked={gameSettings.alertRoundEnd ?? false}
                          onChange={(e) => setGameSettings(prev => ({
                            ...prev,
                            alertRoundEnd: e.target.checked,
                          }))}
                        />
                        <span>Round end alerts</span>
                      </label>
                    )}
                    <label className="d-flex align-items-center gap-xs">
                      <input
                        className="checkbox"
                        type="checkbox"
                        checked={gameSettings.alertGameEnd ?? false}
                        onChange={(e) => setGameSettings(prev => ({
                          ...prev,
                          alertGameEnd: e.target.checked,
                        }))}
                      />
                      <span>Game end alerts</span>
                    </label>
                  </div>
                )}

                {saveMessage && (
                  <div className={`message ${saveMessage.type}`}>
                    {saveMessage.text}
                  </div>
                )}

                <div className="d-flex justify-content-end">
                  <button
                    onClick={handleSaveSettings}
                    disabled={isSavingSettings}
                    className="btn"
                  >
                    {isSavingSettings ? 'Saving...' : '💾 Save Discord Settings'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Player Discord Links Section */}
      {enabledPlayers.length > 0 && (
        <div className="card card--bg-midnight card--border card--border-navy">
          <h3>Player Discord Links</h3>
          {(() => {
            const needsManual = enabledPlayers.filter(p => !p.discord && p.discordAutoLinkError).length;
            return needsManual > 0 ? (
              <p className="d-block mb-xs px-xs py-2xs bg-amber bg-o-50 border-amber rounded-xs text-amber fs-sm">
                {needsManual} player{needsManual > 1 ? 's' : ''} could not be auto-linked and require{needsManual === 1 ? 's' : ''} manual linking below.
              </p>
            ) : null;
          })()}
          <p className="field__hint">
            {selectedGuild
              ? 'Select a Discord member from the server to link to each player.'
              : 'Select a Discord server above to enable player linking.'}
          </p>

          {isLoadingMembers ? (
            <p>Loading Discord members...</p>
          ) : (
            <div className="table-wrap">
              <table className="table table--sm">
                <thead>
                  <tr>
                    <th>Player</th>
                    <th>Discord User</th>
                    <th className="table__cell--actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {enabledPlayers.map((player) => (
                      <tr key={player.playerId}>
                        <td>{player.characterName || player.playerId.slice(0, 8)}</td>
                        <td>
                          {manualLinkPlayer === player.playerId ? (
                            <div className="dropdown dropdown--block is-open">
                              <input
                                type="text"
                                placeholder="Search Discord members..."
                                value={selectedMember ? selectedMember.displayName : memberSearch}
                                onChange={(e) => {
                                  setMemberSearch(e.target.value);
                                  setSelectedMember(null);
                                  setShowMemberDropdown(true);
                                }}
                                onFocus={() => setShowMemberDropdown(true)}
                                className="input input--sm"
                              />
                              {showMemberDropdown && !selectedMember && memberSearch && (
                                <div className="dropdown__menu">
                                  {filteredMembers.length === 0 ? (
                                    <div className="dropdown__item text-center text-slate p-xl">No members found</div>
                                  ) : (
                                    filteredMembers.slice(0, 10).map((member) => (
                                      <div
                                        key={member.id}
                                        className="dropdown__item"
                                        onClick={() => {
                                          setSelectedMember(member);
                                          setMemberSearch('');
                                          setShowMemberDropdown(false);
                                        }}
                                      >
                                        {member.avatar && (
                                          <img
                                            src={`https://cdn.discordapp.com/avatars/${member.id}/${member.avatar}.png?size=32`}
                                            alt=""
                                            className="rounded-full"
                                          />
                                        )}
                                        <span className="fw-medium">{member.displayName}</span>
                                        {member.displayName !== member.username && (
                                          <span className="fs-xs text-slate">@{member.username}</span>
                                        )}
                                      </div>
                                    ))
                                  )}
                                </div>
                              )}
                              {selectedMember && (
                                <div className="badge badge--discord">
                                  ✓ {selectedMember.displayName}
                                </div>
                              )}
                            </div>
                          ) : player.discord ? (
                            <span className="text-green">
                              {player.discord.discordUsername}
                              <span className="ff-mono fs-xs text-slate">({player.discord.discordId})</span>
                            </span>
                          ) : player.discordAutoLinkError ? (
                            <span className="text-amber" title={player.discordAutoLinkError}>
                              {player.discordAutoLinkError.includes('administrator') ? 'Discord elevated (admin)' : 'Discord offline'}
                            </span>
                          ) : (
                            <span className="text-slate">Not linked</span>
                          )}
                        </td>
                        <td className="table__cell--actions">
                          {manualLinkPlayer === player.playerId ? (
                            <div className="action-buttons">
                              <button
                                onClick={() => handleManualLink(player.playerId)}
                                disabled={isManualLinking || !selectedMember}
                                className="btn btn--sm"
                              >
                                {isManualLinking ? '...' : 'Save'}
                              </button>
                              <button
                                onClick={() => {
                                  setManualLinkPlayer(null);
                                  setSelectedMember(null);
                                  setMemberSearch('');
                                  setShowMemberDropdown(false);
                                }}
                                className="btn btn--sm btn--outline btn--slate"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => {
                                setManualLinkPlayer(player.playerId);
                                if (player.discord) {
                                  const existingMember = members.find(m => m.id === player.discord?.discordId);
                                  if (existingMember) {
                                    setSelectedMember(existingMember);
                                  }
                                } else {
                                  setSelectedMember(null);
                                }
                                setMemberSearch('');
                                setShowMemberDropdown(false);
                              }}
                              disabled={!selectedGuild || isLoadingMembers}
                              className="btn btn--sm btn--outline btn--slate"
                            >
                              {isLoadingMembers ? 'Loading...' : (player.discord ? 'Edit' : 'Link')}
                            </button>
                          )}
                        </td>
                      </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );

  function handleManualLink(playerId: string) {
    if (!socket || !selectedGame || !selectedMember) return;

    setIsManualLinking(true);

    socket.emit(SocketEvent.DISCORD_ADMIN_LINK_PLAYER, {
      roomCode: selectedGame.roomCode,
      playerId,
      discordId: selectedMember.id,
      discordUsername: selectedMember.displayName,
    });
  }
}
